import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { calendarConnectionsService } from '../api/calendar-connections.service';
import { CALENDAR_CONNECTIONS_KEYS, EXTERNAL_CALENDAR_EVENTS_KEYS } from './queryKeys';

/**
 * Triggers a manual resync of a calendar connection.
 *
 * The endpoint is synchronous (real Google/Microsoft calls + DB upsert) and can
 * take 30-60s on a cold cursor — callers must render an explicit pending state
 * (spinner + disabled button), not an optimistic toast. On success it
 * invalidates both the connections list (lastSyncedAt/lastSyncError) and the
 * external events of the active tenant for immediate feedback without waiting
 * for the SignalR broadcast.
 */
export function useStartManualCalendarSync() {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (connectionId: string) => calendarConnectionsService.syncConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALENDAR_CONNECTIONS_KEYS.all });
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: EXTERNAL_CALENDAR_EVENTS_KEYS.root(orgId) });
      }
    },
  });
}
